/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useLocale } from '@/hooks/useLocale';
import { useLocationsParams } from './useLocationsParams';
import { useMemo } from 'react';
import { type BreadcrumbItemType } from '@/components/AZCustomComponent/BreadcrumbSTARC/utils/isBreadcrumbModel';
import { locationsPrefix } from '../constants/locationsPrefix';
import { useLabels } from '@/hooks/useLabels';
import { useStoreDetails } from '../api/getStoreDetails';
import { useCityStoresInfinite } from '../api/getCityStores';
import { type LocationsPageType } from '../interface/types';
import { useStateStore } from '../api/getStateStore';

const labelMap = {
  lblLocationsHome: 'label_locations_breadcrumbs_home',
};

type UseLocationBreadcrumbsDataProps = {
  pageType: LocationsPageType;
};

export const useLocationBreadcrumbsData = ({ pageType }: UseLocationBreadcrumbsDataProps) => {
  const locale = useLocale();
  const { state, city, address } = useLocationsParams();

  const { data: storeDetails } = useStoreDetails({
    enabled: pageType === 'STORE_DETAILS',
  });

  const { data: stateData } = useStateStore({ enabled: pageType === 'STATE' });

  const { data: cityData } = useCityStoresInfinite({ enabled: pageType === 'CITY' });

  const labels = useLabels(labelMap);
  return useMemo(() => {
    const baseUrl = `/${locationsPrefix[locale]}`;

    const breadcrumbItems: BreadcrumbItemType[] = [
      {
        url: baseUrl,
        label: labels.lblLocationsHome,
      },
    ];

    if (pageType === 'HOME') {
      return breadcrumbItems;
    }

    if (pageType === 'STATE') {
      breadcrumbItems.push({
        url: `${baseUrl}/${state}.html`,
        label: stateData?.response.entities.at(0)?.address?.region,
      });
      return breadcrumbItems;
    }

    if (pageType === 'CITY') {
      breadcrumbItems.push({
        url: `${baseUrl}/${state}.html`,
        label: cityData?.pages.at(0)?.response.entities.at(0)?.address?.region,
      });

      breadcrumbItems.push({
        url: `${baseUrl}/${state}/${city}.html`,
        label: cityData?.pages.at(0)?.response.entities.at(0)?.address?.city,
      });

      return breadcrumbItems;
    }

    breadcrumbItems.push({
      url: `${baseUrl}/${state}.html`,
      label: storeDetails?.address?.region,
    });

    breadcrumbItems.push({
      url: `${baseUrl}/${state}/${city}.html`,
      label: storeDetails?.address?.city,
    });

    breadcrumbItems.push({
      url: `${baseUrl}/${state}/${city}/${address}.html`,
      label: storeDetails?.name,
    });

    return breadcrumbItems;
  }, [
    locale,
    pageType,
    state,
    address,
    city,
    cityData?.pages,
    stateData?.response.entities,
    labels.lblLocationsHome,
    storeDetails?.name,
    storeDetails?.address?.city,
    storeDetails?.address?.region,
  ]);
};
